import * as React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import Link from 'next/link';
import { withRouter } from 'next/router';
import Slider, { CustomArrowProps } from 'react-slick';
import moment from 'moment';
import { isNil, noop, get } from 'lodash';
import cookie from 'js-cookie';

import EventEmitter from '../../../lib/eventEmitter';
import {
  addressString,
  checkStatusLive,
  formatBathrooms,
  getPhotosByRoom,
} from '../../../lib/helpers';
import NumberElement from '../NumberElement';
import FavoriteButton from '../FavoriteButton';
import { formatAsDollars, formatUrlAddress } from '../../../lib/helpers';
import { MINIMUM_SUPPORTED_PRICE } from '.././../../lib/constants';
import track from '../../../lib/track';
import { BackwardsLeftIcon, ForwardRightIcon } from '../Icons';

import styles from '../../../styles/sass/components/ListingPreview.module.scss';

export interface Props {
  allowFavorite: boolean;
  compact?: boolean;
  dispatch: any;
  investment?: boolean;
  listing: any;
  router: any;
  token?: string;
  selected?: boolean;
  onCardSelect?: Function;
  photosByRoom?: string;
  disableImageSlider?: boolean;
}

const LeftIcon = ({
  currentSlide,
  slideCount,
  ...props
} = CustomArrowProps) => {
  return (
    <div
      {...props}
      onClick={e => {
        e.stopPropagation();
        props.onClick();
      }}
    >
      <BackwardsLeftIcon />
    </div>
  );
};

const RightIcon = ({
  currentSlide,
  slideCount,
  ...props
} = CustomArrowProps) => {
  return (
    <div
      {...props}
      onClick={e => {
        e.stopPropagation();
        props.onClick();
      }}
    >
      <ForwardRightIcon />
    </div>
  );
};

const settings = {
  infinite: true,
  initialSlide: 0,
  lazyLoad: 'ondemand',
  nextArrow: <RightIcon />,
  prevArrow: <LeftIcon />,
  slidesToScroll: 1,
  slidesToShow: 1,
  swipeToSlide: true,
};

export class CardListingPreview extends React.Component<Props> {
  listingList = () => {
    return this.props.listing.photoMediumList.map(
      (photo: any, index: number) => (
        <div className="slick-slide" key={index}>
          <img
            src={photo}
            className={styles.slickSlideImage}
            alt={`listing-photo--${index}`}
          />
        </div>
      ),
    );
  };

  renderContent = () => {
    const {
      allowFavorite,
      dispatch,
      listing,
      token,
      selected,
      onCardSelect,
      disableImageSlider,
    } = this.props;
    const activeStatus = checkStatusLive(listing);
    let openHouseOrUpdate = `UPDATED ${moment(listing.updateDate)
      .fromNow()
      .toLocaleUpperCase()}`;
    if (
      listing.openHouses &&
      listing.openHouses.length > 0 &&
      moment(listing.openHouses[0].date) > moment()
    ) {
      const { date, duration } = listing.openHouses[0];
      const time = moment.utc(date);
      openHouseOrUpdate = `OPEN ${moment(time)
        .format('ddd')
        .toLocaleUpperCase()} ${time.format('h:mma')} -
        ${time.add(duration, 'minutes').format('h:mma')}`;
    }

    return (
      <a target="_blank">
        {!isNil(selected) && (
          <button
            className={
              selected
                ? `${styles.listingPreviewSelectButton} ${styles.selectedIcon}`
                : `${styles.listingPreviewSelectButton} ${styles.unselectedIcon}`
            }
            onClick={(e: any) => {
              e.preventDefault();
              !isNil(onCardSelect) ? onCardSelect(listing) : noop();
            }}
          />
        )}
        <div
          className={`${styles.listingImgContainer} listing-img-container`}
          style={{
            opacity: !isNil(selected) && !selected ? 0.3 : 1,
          }}
        >
          <div className={styles.detailsContainer}>
            <div className={`${styles.detailsBox} ${styles.openHouse}`}>
              <p className={styles.detailsText}>{openHouseOrUpdate}</p>
            </div>
          </div>
          {allowFavorite && activeStatus && (
            <FavoriteButton
              listingId={listing.id}
              light={false}
              card
              containerStyle={{
                position: 'absolute',
                top: 10,
                right: 10,
                padding: 0,
                zIndex: 7,
              }}
            />
          )}
          <div className={`${styles.sliderWrapper} slider-wrapper`}>
            <div className={styles.slideOverlay}>&nbsp;</div>
            {disableImageSlider ? (
              <div className="slick-slide">
                <img
                  src={listing.photoMedium}
                  className={styles.slickSlideImage}
                  alt={`listing-photo`}
                />
              </div>
            ) : (
              <Slider {...settings}>{this.listingList()}</Slider>
            )}
          </div>
        </div>
        <div
          className={`${styles.listingText} listing-text`}
          style={{
            opacity: !isNil(selected) && !selected ? 0.3 : 1,
          }}
        >
          <div className={styles.firstLine}>
            <h2>
              <NumberElement
                val={listing.salePrice ? listing.salePrice : listing.listPrice}
                prefix="$"
                decimalScale={0}
              />
            </h2>
            <p>
              {listing.noBedrooms}br
              <span className={`listing-pipe`}>|</span>
              {formatBathrooms(listing)}ba
              <span className={`listing-pipe`}>|</span>
              {listing.squareFeet ? (
                <NumberElement val={listing.squareFeet} decimalScale={0} />
              ) : (
                '--'
              )}
              sf
            </p>
          </div>
          <p>
            {addressString(listing, false)}
            {listing.noUnits &&
              listing.noUnits > 1 &&
              listing.propType === 'Multi-Family' &&
              ` (${listing.noUnits} units)`}
          </p>
        </div>
      </a>
    );
  };

  render() {
    const { listing, selected, compact, photosByRoom } = this.props;

    return (
      <div
        className={
          compact
            ? `${styles.listingPreview} listing-preview ${styles.compact}`
            : `${styles.listingPreview} listing-preview`
        }
        onMouseDown={() => {
          track.buttonClick(
            `Listing Preview: { Listing id: ${listing.id} }`,
            this.props.router.pathname,
            `/listing/r/${listing.id}/${formatUrlAddress(
              listing,
            )}${getPhotosByRoom(photosByRoom)}`,
          );
        }}
      >
        {!isNil(selected) && !selected ? (
          this.renderContent()
        ) : (
          <Link
            as={`/listing/r/${listing.id}/${formatUrlAddress(
              listing,
            )}${getPhotosByRoom(photosByRoom)}`}
            href={`/listing/r?listingId=${listing.id}${getPhotosByRoom(
              photosByRoom,
            )}`}
            prefetch={false}
          >
            {this.renderContent()}
          </Link>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  token: state.auth.token,
});

export default connect(mapStateToProps)(withRouter(CardListingPreview));
